﻿import { FilterGroup } from '@fe/core';
import { Reveal } from '@progress/kendo-react-animation';
import { Button } from '@progress/kendo-react-buttons';
import { SvgIcon } from '@progress/kendo-react-common';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { TextBox, TextBoxHandle } from '@progress/kendo-react-inputs';
import React, { useCallback, useEffect } from 'react';
import { CheckmarkOutline, CloseOutline, EditOutline, TrashOutline, UserGroupOutline, UserOutline } from '../../../../../../icons/icons';
import { FilterGroupType } from '../../../../../../types/filter-group';
import MarkerText from '../../../../../components/inputs/MarkerText';
import { useAppSelector } from '../../../../../hooks/store-hooks';

const TAKE_INITIAL = 7;
export default function FilterGroupItem(props: {
    item: FilterGroup;
    type: FilterGroupType;
    searchTerm?: string;
    onUpdate?: (item: FilterGroup, type: FilterGroupType) => void;
    onDelete?: (item: FilterGroup) => void;
    isEditing?: boolean;
    onEditingChange?: (editing: boolean) => void;
    allGroups?: FilterGroup[];
}) {
    const { item, isEditing = false, onEditingChange, allGroups = [] } = props;
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [membersToAdd, setMembersToAdd] = React.useState<string>('');
    const [displayMembers, setDisplayMembers] = React.useState(item.members.slice(0, TAKE_INITIAL));
    const [duplicatesFiltered, setDuplicatesFiltered] = React.useState<number | null>(null);

    const [showConfirmDelete, setShowConfirmDelete] = React.useState(false);
    const [isEditingName, setIsEditingName] = React.useState(false);
    const [editedName, setEditedName] = React.useState(item.name);
    const nameInputRef = React.useRef<TextBoxHandle>(null);
    const canEditCompanyFilterGroups = useAppSelector((x) => x.user.rights.canEditCompanyFilterGroups);
    const companyFeaturesEnabled = useAppSelector((x) => x.user.rights.companyFeaturesEnabled);

    const updateMembers = useCallback(() => {
        const newMembers = membersToAdd
            .split(/[\n,\s]+/)
            .map((m) => m.trim())
            .filter((m) => m.length > 0);

        const totalBeforeFilter = newMembers.length;
        const existingMembersSet = new Set(item.members.map((m) => m.text.toUpperCase()));
        const uniqueNewMembers = new Set<string>();

        newMembers.forEach((m) => {
            const upperText = m.toUpperCase();
            if (!existingMembersSet.has(upperText) && !uniqueNewMembers.has(upperText)) {
                uniqueNewMembers.add(m);
            }
        });

        const duplicatesCount = totalBeforeFilter - uniqueNewMembers.size;

        if (duplicatesCount > 0) {
            setDuplicatesFiltered(duplicatesCount);
        } else {
            setDuplicatesFiltered(null);
        }

        const updatedMembers = [...item.members, ...Array.from(uniqueNewMembers).map((text) => ({ text }))];
        props.onUpdate?.({ ...item, members: updatedMembers }, props.type);

        setMembersToAdd('');
    }, [membersToAdd, item, props]);

    const removeMember = useCallback(
        (memberText: string) => {
            const updatedMembers = item.members.filter((m) => m.text !== memberText);
            props.onUpdate?.({ ...item, members: updatedMembers }, props.type);
        },
        [item, props.onUpdate]
    );

    const isNameDuplicate = React.useMemo(() => {
        if (!isEditingName || editedName.trim() === item.name) {
            return false;
        }
        return allGroups.some((g) => g.id !== item.id && g.name.toLowerCase() === editedName.trim().toLowerCase());
    }, [isEditingName, editedName, item.id, item.name, allGroups]);

    const saveNameEdit = useCallback(() => {
        if (!editedName.trim()) {
            setEditedName(item.name);
            setIsEditingName(false);
            return;
        }

        if (isNameDuplicate) {
            setEditedName(item.name);
            setIsEditingName(false);
            return;
        }

        if (editedName.trim() !== item.name) {
            props.onUpdate?.({ ...item, name: editedName.trim() }, props.type);
        }
        setIsEditingName(false);
    }, [editedName, item, props, isNameDuplicate]);

    const cancelNameEdit = useCallback(() => {
        setEditedName(item.name);
        setIsEditingName(false);
    }, [item.name]);

    const handleNameKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                saveNameEdit();
            } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelNameEdit();
            }
        },
        [saveNameEdit, cancelNameEdit]
    );

    const handleMembersKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                updateMembers();
            }
        },
        [updateMembers]
    );

    const pluralize = (count: number, singular: string, plural?: string) => {
        const pluralRules = new Intl.PluralRules('en-US');
        const rule = pluralRules.select(count);
        return rule === 'one' ? singular : plural || `${singular}s`;
    };

    useEffect(() => {
        if (isExpanded) {
            setDisplayMembers(item.members);
        } else {
            setDisplayMembers(item.members.slice(0, TAKE_INITIAL));
        }
    }, [isExpanded, item]);

    useEffect(() => {
        if (isEditingName && nameInputRef.current) {
            nameInputRef.current.element?.focus();
        }
    }, [isEditingName]);

    useEffect(() => {
        setDuplicatesFiltered(null);
    }, [isEditing]);

    return (
        <div className={'d-flex flex-column k-card m-1 flex-shrink-0'}>
            <div className={'d-flex justify-content-between align-items-center p-2'}>
                <div className={'d-flex gap-2 align-items-center flex-grow-1'}>
                    {item.isCompany ? (
                        <div className={'fe-badge-company'}>
                            <SvgIcon icon={UserGroupOutline} />
                            Company
                        </div>
                    ) : (
                        <>
                            {companyFeaturesEnabled && (
                                <div className={'fe-badge-secondary'}>
                                    <SvgIcon icon={UserOutline} />
                                    Personal
                                </div>
                            )}
                        </>
                    )}
                    <div className={'d-flex flex-column'}>
                        {isEditingName ? (
                            <>
                                <TextBox
                                    ref={nameInputRef}
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.value as string)}
                                    onBlur={saveNameEdit}
                                    onKeyDown={handleNameKeyDown}
                                    className={'!k-max-w-fit'}
                                    valid={!isNameDuplicate}
                                />
                                {isNameDuplicate && (
                                    <span className={'text-danger small mt-1'} style={{ fontSize: '.75em' }}>
                                        A group with this name already exists
                                    </span>
                                )}
                            </>
                        ) : (
                            <MarkerText highlight={props.searchTerm} className={'clickable'} onClick={() => setIsEditingName(true)}>
                                {item.name}
                            </MarkerText>
                        )}
                    </div>
                    <div className={'secondary-text'}>
                        {item.members.length} {pluralize(item.members.length, 'filter')}
                    </div>
                </div>
                <div className={'d-flex gap-2'}>
                    <Button
                        size={'small'}
                        fillMode={'outline'}
                        svgIcon={isEditing ? CloseOutline : EditOutline}
                        togglable={true}
                        disabled={!canEditCompanyFilterGroups && item.isCompany}
                        selected={isEditing}
                        title={isEditing ? 'Stop editing' : 'Edit filters'}
                        onClick={() => onEditingChange?.(!isEditing)}></Button>
                    <Button
                        size={'small'}
                        fillMode={'outline'}
                        svgIcon={TrashOutline}
                        disabled={!canEditCompanyFilterGroups && item.isCompany}
                        title={'Delete filter group'}
                        onClick={() => setShowConfirmDelete(true)}
                        themeColor={'error'}></Button>
                </div>
            </div>

            <div className={'border-top d-flex px-2 py-2 gap-1 align-items-center overflow-y-auto overflow-x-hidden flex-wrap'}>
                {displayMembers.map((m) => (
                    <div key={m.text} className={'fe-badge-outline d-flex align-items-center'}>
                        {m.text}
                        {isEditing && (
                            <SvgIcon icon={CloseOutline} size={'xsmall'} className={'clickable'} onClick={() => removeMember(m.text)} />
                        )}
                    </div>
                ))}
                {item.members.length > TAKE_INITIAL && (
                    <div
                        className={'text-secondary align-self-center clickable'}
                        style={{ fontSize: '.75em' }}
                        onClick={() => setIsExpanded(!isExpanded)}>
                        {isExpanded ? <span>collapse</span> : <span>+{item.members.length - displayMembers.length} more</span>}
                    </div>
                )}
                {item.members.length === 0 && (
                    <div className={'text-secondary'} style={{ fontSize: '.75em' }}>
                        No filters added
                    </div>
                )}
            </div>
            <Reveal>
                {isEditing && (
                    <div className={'d-flex flex-column gap-2 px-2 border-top pt-2'}>
                        <Reveal style={{ display: 'contents' }} transitionEnterDuration={150} transitionExitDuration={150}>
                            {duplicatesFiltered !== null && duplicatesFiltered > 0 && (
                                <div className={'k-messagebox k-messagebox-info small m-0 mt-1'} style={{ fontSize: '.85em' }}>
                                    <span className={'k-icon k-i-info'}></span>
                                    {duplicatesFiltered} duplicate {duplicatesFiltered === 1 ? 'filter was' : 'filters were'} filtered out.
                                    Filters must be unique per group.
                                </div>
                            )}
                        </Reveal>

                        <div className={'d-flex gap-2'}>
                            <TextBox
                                placeholder="Add filters (comma or space separated)"
                                className={'flex-grow-1'}
                                size={'small'}
                                value={membersToAdd}
                                onChange={(e) => setMembersToAdd((e.value as string).toUpperCase())}
                                onKeyDown={handleMembersKeyDown}></TextBox>
                            <Button
                                size={'small'}
                                svgIcon={CheckmarkOutline}
                                themeColor={'primary'}
                                className={'px-2'}
                                onClick={updateMembers}>
                                Add
                            </Button>
                        </div>
                        <div className={'secondary-text'}>You can paste multiple filters separated by commas or spaces</div>
                    </div>
                )}
            </Reveal>

            {showConfirmDelete && (
                <Dialog title={'Confirm Delete'} onClose={() => setShowConfirmDelete(false)}>
                    <p>Are you sure you want to delete the filter group "{item.name}"?</p>
                    <DialogActionsBar>
                        <Button onClick={() => setShowConfirmDelete(false)}>Cancel</Button>
                        <Button
                            themeColor={'error'}
                            onClick={() => {
                                props.onDelete?.(item);
                                setShowConfirmDelete(false);
                            }}>
                            Delete
                        </Button>
                    </DialogActionsBar>
                </Dialog>
            )}
        </div>
    );
}
